import {
	DefaultInsightsConfig,
	InsightsConfig,
} from '../interfaces/firestore-config.interface';

/** Do not poll Krista faster than this. */
export const InsightPollMinMillis = 2_000;

/** Do not wait longer than this between polls. */
export const InsightPollMaxMillis = 5_000;

/** Samples required before the measured average drives the interval. */
export const InsightPollSampleThreshold = 10;

/** Interval used until `requestCount` reaches the sample threshold. */
export const InsightPollColdStartMillis = 5_000;

/**
 * Poll gap in milliseconds. After 10 finished requests, half the average
 * duration, clamped to 2–5 seconds. Before that, a fixed 5 seconds.
 * Stop polling when the user leaves the page; Krista keeps the run.
 */
export function insightPollIntervalMillis(
	config?: Pick<
		InsightsConfig,
		'requestCount' | 'requestDurationAvgMillis'
	> | null,
): number {
	if (!config || config.requestCount < InsightPollSampleThreshold) {
		return InsightPollColdStartMillis;
	}

	const half = config.requestDurationAvgMillis / 2;
	if (!Number.isFinite(half)) return InsightPollColdStartMillis;

	return Math.min(InsightPollMaxMillis, Math.max(InsightPollMinMillis, half));
}

/**
 * Rolling average of generation time. `requestCount` is the number of
 * finished requests included in the average.
 */
export function nextInsightDurationStats(
	current: Pick<InsightsConfig, 'requestCount' | 'requestDurationAvgMillis'>,
	durationMillis: number,
): Pick<InsightsConfig, 'requestCount' | 'requestDurationAvgMillis'> {
	const requestCount = (current.requestCount ?? 0) + 1;
	const total =
		(current.requestDurationAvgMillis ?? 0) * (current.requestCount ?? 0) +
		durationMillis;

	return {
		requestCount,
		requestDurationAvgMillis: Math.round(total / requestCount),
	};
}

export function insightsConfigOrDefault(
	config: Partial<InsightsConfig> | null | undefined,
): InsightsConfig {
	return {
		enabled: config?.enabled === true,
		requestDurationAvgMillis:
			config?.requestDurationAvgMillis ??
			DefaultInsightsConfig.requestDurationAvgMillis,
		requestCount:
			config?.requestCount ?? DefaultInsightsConfig.requestCount,
	};
}
